import Cookies from 'js-cookie';

export type RecentFleet = {
  id: string;
  name: string;
};

type StoredRecentFleets = {
  user: string;
  fleets: RecentFleet[];
};

export const RECENT_FLEETS_LIMIT = 6;

const ACTIVE_FLEET_COOKIE = 'fleetId';
const RECENT_FLEETS_COOKIE = 'recentFleets';
const COOKIE_OPTIONS = { expires: 365, secure: true };
const MAX_FLEET_NAME_LENGTH = 60;

export function getActiveFleetId(): string {
  return Cookies.get(ACTIVE_FLEET_COOKIE) || '';
}

export function setActiveFleetId(fleetId: string): void {
  Cookies.set(ACTIVE_FLEET_COOKIE, fleetId, COOKIE_OPTIONS);
}

export function clearActiveFleetId(): void {
  Cookies.remove(ACTIVE_FLEET_COOKIE);
}

function readRecentFleets(): StoredRecentFleets | null {
  const raw = Cookies.get(RECENT_FLEETS_COOKIE);

  if (!raw) {
    return null;
  }

  try {
    const stored = JSON.parse(raw);

    if (!stored || !Array.isArray(stored.fleets)) {
      return null;
    }

    return { user: `${stored.user || ''}`, fleets: stored.fleets };
  } catch (e) {
    return null;
  }
}

// recently opened fleets survive a logout (unlike the active fleet id),
// so they are kept per user to not expose or restore somebody else's fleets
// on a shared browser
export function getRecentFleets(user: string): RecentFleet[] {
  const stored = readRecentFleets();

  if (!user || !stored || stored.user !== user) {
    return [];
  }

  return stored.fleets
    .filter((fleet: RecentFleet) => !!fleet?.id && !!fleet?.name)
    .slice(0, RECENT_FLEETS_LIMIT);
}

export function getLastOpenedFleetId(user: string): string {
  return getRecentFleets(user)[0]?.id || '';
}

export function rememberOpenedFleet(user: string, fleet: RecentFleet): RecentFleet[] {
  if (!user || !fleet.id || !fleet.name) {
    return getRecentFleets(user);
  }

  const fleets = [
    { id: fleet.id, name: fleet.name.slice(0, MAX_FLEET_NAME_LENGTH) },
    ...getRecentFleets(user).filter((item) => item.id !== fleet.id),
  ].slice(0, RECENT_FLEETS_LIMIT);

  Cookies.set(RECENT_FLEETS_COOKIE, JSON.stringify({ user, fleets }), COOKIE_OPTIONS);

  return fleets;
}
